import React, { useState, useMemo, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography } from "@mui/material";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell, TitleCaseCell } from "../../CustomizedTable/CustomCells/CustomCells";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import LoadIdCell from "../../CustomizedTable/CustomCells/OrderIdCell/LoadIdCell";
import BrokerageStatusCell from "../../CustomizedTable/CustomCells/BrokerageStatusCell/BrokerageStatusCell";
import ModeWithTempCell from "../../CustomizedTable/CustomCells/ModeWithTempCell/ModeWithTempCell";
import CarrierRepNameCell from "../../CustomizedTable/CustomCells/CarrierRepNameCell/CarrierRepNameCell";
import WeightCell from "../../CustomizedTable/CustomCells/WeightCell/WeightCell";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import BrokerageStatusFilter from "../../CustomizedTable/Filters/BrokerageStatusFilter/BrokerageStatusFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import SelectColumnFilter from "../../CustomizedTable/Filters/SelectColumnFilter/SelectColumnFilter";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function CarrierProfileHistoricalLoads({
  activeCarrier,
  handleTableAction
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [search, setSearch] = useState("");
  const [historicalLoads, setHistoricalLoads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(90);

  const loads = useMemo(() => historicalLoads, [historicalLoads]);

  const options = [
    {
      name: "Past 30 Days",
      value: 30
    },
    {
      name: "Past 60 Days",
      value: 60
    },
    {
      name: "Past 90 Days",
      value: 90
    },
    {
      name: "Past 6 Months",
      value: 180
    },
    {
      name: "Past 1 Year",
      value: 365
    },
    {
      name: "Past 2 Years",
      value: 730
    },
    {
      name: "Past 3 Years",
      value: 1095
    },
    {
      name: "Past 5 Years",
      value: 1825
    }
  ];

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "brokerageStatus",
        Filter: BrokerageStatusFilter,
        filter: "multi",
        width: 100,
        Cell: BrokerageStatusCell,
      },
      {
        Header: "Load",
        accessor: "id",
        filter: "fuzzyText",
        width: 90,
        Cell: LoadIdCell,
      },
      {
        Header: "Mode",
        accessor: "mode",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 160,
        Cell: ModeWithTempCell,
      },
      {
        Header: "Origin City",
        accessor: "originCity",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell,
      },
      {
        Header: "OS",
        accessor: "originState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 90,
        Cell: DefaultCell,
      },
      {
        Header: "Pick Up",
        accessor: "origSchedArriveEarly",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        lateDate: "origSchedArriveLate",
        stopType: "Origin",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Dest City",
        accessor: "destinationCity",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell,
      },
      {
        Header: "DS",
        accessor: "destinationState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 90,
        Cell: DefaultCell,
      },
      {
        Header: "Delivery",
        accessor: "destSchedArriveEarly",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        lateDate: "destSchedArriveLate",
        stopType: "Destination",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Customer",
        accessor: "customer",
        filter: "fuzzyText",
        width: 320,
        Cell: DefaultCell,
      },
      {
        Header: "Carrier LH",
        accessor: "carrierLineHaul",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 130,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier FSC",
        accessor: "carrierFuelCost",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier Pay",
        accessor: "carrierTotalPay",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier Rep",
        accessor: "dispatcherUserId",
        filter: "fuzzyText",
        width: 140,
        Cell: CarrierRepNameCell,
      },
      {
        Header: "Commodity",
        accessor: "commodity",
        filter: "fuzzyText",
        width: 140,
        Cell: TitleCaseCell,
      },
      {
        Header: "Weight",
        accessor: "weight",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 110,
        Cell: WeightCell,
      },
      {
        Header: "Payment Date",
        accessor: "latestPayDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Payment Amount",
        accessor: "netpay",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 160,
        Cell: CurrencyCell,
      },
      {
        Header: "Check / ACH Number",
        accessor: "checkNumber",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell,
      },
    ],
    []
  );

  useEffect(() => {
    if (activeCarrier.id) {
      setIsLoading(true);
      CarrierService.GetCarrierLoads(instance, activeCarrier.id, selectedOption).then(res => {
        setHistoricalLoads(res.loads);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [activeCarrier, selectedOption]);

  return (
    <Box className="Carrier-profile-component">
      <Box className="Carrier-profile-component-header">
        <Typography variant="h3" color="black_opacity.100">
          Load History
        </Typography>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ marginRight: "10px" }}>
            <PrimarySelect
              options={options.map((o) => (o))}
              onChange={setSelectedOption}
              value={selectedOption}
              width="160px"
              variant="standard"
              nameKey="name"
              valueKey="value"
              disabled={isLoading}
            />
          </Box>
          <PrimaryTextField
            placeholder={`Search ${loads.length} results`}
            onChange={setSearch}
            value={search}
            isSearch
          />
        </Box>
      </Box>
      <Box>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <CircularProgress sx={{ color: "primary.60" }} size={40} />
          </Box>
        ) : (
          <CustomizedTable
            id="carrier-profile-historical-loads"
            columns={columns}
            data={loads}
            tableSearch={search}
            actionCallback={handleTableAction}
          />
        )}
      </Box>
    </Box>
  );
}

CarrierProfileHistoricalLoads.propTypes = {
  activeCarrier: PropTypes.object.isRequired,
  handleTableAction: PropTypes.func
};